import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Copy, CheckCircle2, XCircle, Loader2, Trash2, Save, Info } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Card } from "@/components/ui-helpers";

const PROVIDERS = {
  stripe: {
    name: "Stripe (card payments)", modes: [["test", "Test"], ["live", "Live"]],
    plain: [["publishable_key", "Publishable key", "pk_test_… or pk_live_…"]],
    secret: [["secret_key", "Secret key", "sk_test_… or sk_live_…"], ["webhook_secret", "Webhook signing secret", "whsec_…"]],
    help: "Find your keys in the Stripe Dashboard → Developers → API keys. Add a webhook endpoint for the event checkout.session.completed and paste its signing secret here.",
  },
  paypal: {
    name: "PayPal", modes: [["sandbox", "Sandbox"], ["live", "Live"]],
    plain: [["client_id", "Client ID", "From your PayPal REST app"], ["webhook_id", "Webhook ID", "From the app's Webhooks section"]],
    secret: [["secret", "Secret", "From your PayPal REST app"]],
    help: "Create a REST app at developer.paypal.com → Apps & Credentials. Add a webhook for CHECKOUT.ORDER.APPROVED and PAYMENT.CAPTURE.COMPLETED and paste its Webhook ID here.",
  },
};

const chip = (status) => status === "live" ? ["Live", "bg-emerald-50 text-emerald-700 border-emerald-200"]
  : status === "test" ? ["Test mode", "bg-amber-50 text-amber-700 border-amber-200"] : ["Off", "bg-slate-100 text-slate-500 border-slate-200"];

function ProviderCard({ id, cfg, webhook, onSaved }) {
  const P = PROVIDERS[id];
  const [f, setF] = useState({});
  const [busy, setBusy] = useState("");
  const [test, setTest] = useState(cfg.last_test || null);
  useEffect(() => { setF({ enabled: cfg.enabled, mode: cfg.mode, fee_percent: cfg.fee_percent, fee_label: cfg.fee_label, ...Object.fromEntries(P.plain.map(([k]) => [k, cfg[k] || ""])) }); setTest(cfg.last_test || null); }, [cfg]); // eslint-disable-line
  const set = (patch) => setF((x) => ({ ...x, ...patch }));

  const save = async (extra = {}) => {
    setBusy("save");
    try {
      const body = { ...f, ...extra, fee_percent: Number(f.fee_percent) || 0 };
      Object.keys(body).forEach((k) => { if (body[k] === undefined) delete body[k]; });
      const { data } = await api.put("/payment-settings", { [id]: body, origin_url: window.location.origin });
      toast.success(`${P.name} settings saved`); onSaved(data);
      return true;
    } catch (e) { toast.error(apiErr(e)); return false; } finally { setBusy(""); }
  };
  const removeSecret = async (k, label) => {
    if (!window.confirm(`Remove the saved ${label}?${f.enabled ? " This provider will also be turned off." : ""}`)) return;
    await save({ [k]: "", ...(f.enabled && k !== "webhook_secret" ? { enabled: false } : {}) });
  };
  const runTest = async () => {
    setBusy("test");
    try { const { data } = await api.post("/payment-settings/test", { provider: id }); setTest(data); data.ok ? toast.success(data.message) : toast.error(data.message); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const [label, cls] = chip(cfg.status);

  return (
    <div className="rounded-2xl border border-slate-200 p-4 space-y-3" data-testid={`pay-provider-${id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <h4 className="font-head font-bold text-slate-900">{P.name}</h4>
        <span data-testid={`pay-status-${id}`} className={`text-[11px] font-semibold border px-2 py-0.5 rounded-full ${cls}`}>{label}</span>
        <label className="ml-auto inline-flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
          <input type="checkbox" data-testid={`pay-enabled-${id}`} checked={!!f.enabled} onChange={(e) => set({ enabled: e.target.checked })} className="h-4 w-4 accent-primary" /> On
        </label>
      </div>
      <div className="inline-flex bg-slate-100 rounded-xl p-1" data-testid={`pay-mode-${id}`}>
        {P.modes.map(([m, l]) => (
          <button key={m} type="button" data-testid={`pay-mode-${id}-${m}`} onClick={() => set({ mode: m })}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${f.mode === m ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>
      {P.plain.map(([k, l, ph]) => (
        <label key={k} className="block text-xs font-semibold text-slate-600">{l}
          <input data-testid={`pay-${id}-${k}`} value={f[k] || ""} onChange={(e) => set({ [k]: e.target.value })} placeholder={ph}
            className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-mono font-normal focus:ring-2 focus:ring-primary/30" />
        </label>
      ))}
      {P.secret.map(([k, l, ph]) => (
        <label key={k} className="block text-xs font-semibold text-slate-600">{l}
          <div className="mt-1 flex items-center gap-2">
            <input data-testid={`pay-${id}-${k}`} type="password" autoComplete="new-password" value={f[k] || ""} onChange={(e) => set({ [k]: e.target.value })}
              placeholder={cfg[`${k}_set`] ? `Saved ${cfg[`${k}_hint`]} — type to replace` : ph}
              className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm font-mono font-normal focus:ring-2 focus:ring-primary/30" />
            {cfg[`${k}_set`] && <button type="button" data-testid={`pay-${id}-${k}-remove`} onClick={() => removeSecret(k, l)} title={`Remove ${l}`} className="h-9 w-9 inline-flex items-center justify-center rounded-lg text-rose-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>}
          </div>
        </label>
      ))}
      <div className="grid grid-cols-[110px_1fr] gap-2">
        <label className="block text-xs font-semibold text-slate-600">Processing fee %
          <input data-testid={`pay-${id}-fee`} type="number" min="0" max="10" step="0.1" value={f.fee_percent ?? 0} onChange={(e) => set({ fee_percent: e.target.value })}
            className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-normal" />
        </label>
        <label className="block text-xs font-semibold text-slate-600">Fee label shown to the client
          <input data-testid={`pay-${id}-fee-label`} value={f.fee_label || ""} onChange={(e) => set({ fee_label: e.target.value })}
            className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-normal" />
        </label>
      </div>
      <div className="text-xs text-slate-500">
        <div className="font-semibold text-slate-600 mb-1">Webhook address (paste into the {id === "stripe" ? "Stripe" : "PayPal"} dashboard)</div>
        <div className="flex items-center gap-2">
          <code data-testid={`pay-${id}-webhook-url`} className="flex-1 truncate bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 text-[11px]">{webhook}</code>
          <button type="button" data-testid={`pay-${id}-webhook-copy`} onClick={() => { navigator.clipboard?.writeText(webhook).then(() => toast.success("Webhook address copied")).catch(() => window.prompt("Copy:", webhook)); }}
            className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-primary hover:bg-primary/10"><Copy className="h-4 w-4" /></button>
        </div>
        <p className="mt-1.5 text-[11px] text-slate-400">{P.help}</p>
      </div>
      <div className="flex items-center gap-2 flex-wrap pt-1">
        <button type="button" data-testid={`pay-${id}-save`} onClick={() => save()} disabled={!!busy} className="inline-flex items-center gap-1.5 bg-primary text-white px-4 py-2 rounded-xl text-sm font-semibold disabled:opacity-60">{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>
        <button type="button" data-testid={`pay-${id}-test`} onClick={runTest} disabled={!!busy} className="inline-flex items-center gap-1.5 border border-slate-200 text-slate-700 px-4 py-2 rounded-xl text-sm font-semibold hover:bg-slate-50 disabled:opacity-60">{busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Test connection</button>
        {test && (
          <span data-testid={`pay-${id}-test-result`} className={`inline-flex items-center gap-1 text-xs font-semibold ${test.ok ? "text-emerald-600" : "text-rose-600"}`}>
            {test.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />} {test.ok ? "Connected" : "Not connected"} — {test.message}
          </span>
        )}
      </div>
      <p className="text-[11px] text-slate-400">Test connection checks the saved keys — press Save first after changing them.</p>
    </div>
  );
}

export function OnlinePaymentsCard() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get(`/payment-settings?origin_url=${encodeURIComponent(window.location.origin)}`).then((r) => setCfg(r.data)).catch(() => setCfg(false)); }, []);
  if (cfg === false) return null;
  return (
    <Card className="p-5 space-y-4 mb-5" data-testid="online-payments-card">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0"><CreditCard className="h-5 w-5" /></div>
        <div>
          <h3 className="font-head font-bold text-slate-900">Online Payments</h3>
          <p className="text-sm text-slate-500">Let clients pay invoices and case balances online with a secure "Pay now" link. Paste your own Stripe and/or PayPal keys — switch accounts or go Live anytime, no code changes. Card details never touch the CRM.</p>
        </div>
      </div>
      {!cfg ? <div className="text-sm text-slate-400">Loading…</div> : (
        <>
          <div className="grid lg:grid-cols-2 gap-4">
            {["stripe", "paypal"].map((p) => <ProviderCard key={p} id={p} cfg={cfg[p]} webhook={cfg.webhooks?.[p]} onSaved={setCfg} />)}
          </div>
          <PayOptions cfg={cfg} onSaved={setCfg} />
          <div className="flex items-start gap-2 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5" data-testid="pay-surcharge-note">
            <Info className="h-4 w-4 shrink-0 text-slate-400" />
            <span>Payments are charged in {cfg.currency || "CAD"}; HST is already included in your invoices, so Stripe/PayPal add no tax. The processing fee is added on top and shown to the client before paying — it never reduces what they owe. Canadian surcharge rules: credit-card surcharges are capped (about 2.4%), not allowed on debit cards, and not allowed for Quebec clients. Staying compliant is the firm's responsibility.</span>
          </div>
        </>
      )}
    </Card>
  );
}

// Shared options: automatic receipts, part-payments / deposit on the pay link, "Pay now" on PDFs
function PayOptions({ cfg, onSaved }) {
  const [o, setO] = useState(cfg.options || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setO(cfg.options || {}); }, [cfg]);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/payment-settings", { options: { ...o, min_partial: Number(o.min_partial), deposit_percent: Number(o.deposit_percent) }, origin_url: window.location.origin });
      onSaved(data); toast.success("Payment options saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const Toggle = ({ k, label, hint }) => (
    <label className="flex items-start gap-2.5 text-sm cursor-pointer">
      <input type="checkbox" data-testid={`pay-opt-${k}`} checked={!!o[k]} onChange={(e) => setO({ ...o, [k]: e.target.checked })} className="mt-0.5 h-4 w-4 accent-primary" />
      <span><span className="font-semibold text-slate-800">{label}</span><span className="block text-xs text-slate-500">{hint}</span></span>
    </label>
  );
  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="pay-options">
      <div className="font-semibold text-sm text-slate-800">Payment options</div>
      <div className="grid md:grid-cols-3 gap-4">
        <Toggle k="auto_receipt" label="Automatic receipts" hint="Email the client their receipt as soon as an online payment goes through (needs email connected)." />
        <div className="space-y-2">
          <Toggle k="allow_partial" label="Allow part-payments" hint="Clients can pay the deposit or their own amount from the pay link." />
          {o.allow_partial && <div className="grid grid-cols-2 gap-2 pl-6">
            <label className="text-[11px] text-slate-500">Minimum ($)<input data-testid="pay-opt-min" type="number" min="1" value={o.min_partial ?? 50} onChange={(e) => setO({ ...o, min_partial: e.target.value })} className="mt-0.5 w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm" /></label>
            <label className="text-[11px] text-slate-500">Deposit (% of fees)<input data-testid="pay-opt-deposit" type="number" min="0" max="100" value={o.deposit_percent ?? 50} onChange={(e) => setO({ ...o, deposit_percent: e.target.value })} className="mt-0.5 w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm" /></label>
          </div>}
        </div>
        <Toggle k="pdf_pay_link" label={'"Pay now" on PDFs'} hint="Print a QR code and pay link on invoice and retainer PDFs while money is owing." />
      </div>
      <button data-testid="pay-opt-save" onClick={save} disabled={saving} className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60">{saving ? "Saving…" : "Save options"}</button>
    </div>
  );
}
